import { Switch } from '@repo/design-system/index'
import classNames from 'classnames'
import { SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { CAF_ANALYSIS_TYPE_MAP, Trend } from '../../../../model/caf.interface'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { CAF_icon_list } from '../../../../modules/hooks/webview/caf/chart/utilFunction'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import TrendGraph from './TrendGraph'

interface ITrendGraphSectionPropsType {
  trend: Trend
}

const TrendGraphSection = ({ trend }: ITrendGraphSectionPropsType) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [showDetail, setShowDetail] = useState(false)
  const [activeFilter, setActiveFilter] = useState<string>(CAF_ANALYSIS_TYPE_MAP[5])
  const [graphData, setGraphData] = useState<Trend>(trend)
  const filterList = useMemo(() => ['all', ...Object.keys(trend)], [trend])
  const handleClickFilter = useCallback((anType: SetStateAction<string>) => {
    setActiveFilter(anType)
  }, [])
  // const hasPronunciation = !!trend?.pronunciation
  console.log('🟢trend ====>', trend)

  const hasPronunciation = false

  useEffect(() => {
    if (!showDetail) {
      const nextData = hasPronunciation
        ? {
            complexity: trend.complexity.map((item) => ({ ...item, level: Math.floor(item.level) })),
            accuracy: trend.accuracy.map((item) => ({ ...item, level: Math.floor(item.level) })),
            fluency: trend.fluency.map((item) => ({ ...item, level: Math.floor(item.level) })),
            pronunciation: trend.pronunciation.map((item) => ({ ...item, level: Math.floor(item.level) })),
          }
        : {
            complexity: trend.complexity.map((item) => ({ ...item, level: Math.floor(item.level) })),
            accuracy: trend.accuracy.map((item) => ({ ...item, level: Math.floor(item.level) })),
            fluency: trend.fluency.map((item) => ({ ...item, level: Math.floor(item.level) })),
          }
      setGraphData(nextData)
    }
    if (showDetail) {
      setGraphData(trend)
    }
  }, [trend, showDetail])

  if (isMobile) {
    return (
      <section className="mb-[24px] w-full">
        <div className="mb-[16px] flex flex-row items-center justify-between">
          <div className="text-[16px] font-bold text-black">{t(n_caf.trend.history)}</div>
          <div className="flex flex-row items-center justify-end">
            <span className="mr-[4px] text-[12px] text-gray-500">{t(n_caf.trend.score_detail)}</span>
            <Switch checked={showDetail} setChecked={setShowDetail} />
          </div>
        </div>
        {/* Chip Filter */}
        <ul className={styles.filters.box}>
          {filterList
            .filter((key) => key !== 'pronunciation')
            .map(
              (key): JSX.Element => (
                <li
                  className={styles.filters.item(activeFilter === key)}
                  key={key + 'trend'}
                  onClick={() => handleClickFilter(key)}
                >
                  {key === CAF_ANALYSIS_TYPE_MAP[5] ? t(n_caf.statistic.trend.cafallchip) : key}
                </li>
              )
            )}
        </ul>
        <article className={styles.box}>
          <h2 className={styles.top.title}>
            <img src={CAF_icon_list[activeFilter]} alt="icon" />
            {activeFilter === 'all' ? (
              <span>{t(n_caf.statistic.trend.cafall)}</span>
            ) : (
              <span>{t(n_caf.statistic.trend[activeFilter.toLowerCase()])}</span>
            )}
          </h2>
          <TrendGraph trend={graphData} activeFilter={activeFilter} showDetail={showDetail} />
        </article>
      </section>
    )
  } else {
    return (
      <section className="mb-[24px] w-full">
        <div className="mb-[16px] flex flex-row items-center justify-between">
          <div className="text-[20px] font-bold text-black">{t(n_caf.trend.history)}</div>
          <div className="flex flex-row items-center justify-end">
            <span className="mr-[4px] text-[14px] text-gray-500">{t(n_caf.trend.score_detail)}</span>
            <Switch checked={showDetail} setChecked={setShowDetail} />
          </div>
        </div>
        {/* Chip Filter */}
        <ul className={styles.filters.box}>
          {filterList
            .filter((key) => key !== 'pronunciation')
            .map(
              (key): JSX.Element => (
                <li
                  className={styles.filters.item_pc(activeFilter === key)}
                  key={key + 'trend'}
                  onClick={() => handleClickFilter(key)}
                >
                  {key === CAF_ANALYSIS_TYPE_MAP[5] ? t(n_caf.statistic.trend.cafallchip) : key}
                </li>
              )
            )}
        </ul>
        <article className={styles.box_pc}>
          <h2 className={styles.top.title_pc}>
            <img src={CAF_icon_list[activeFilter]} alt="icon" />
            {activeFilter === 'all' ? (
              <span>{t(n_caf.statistic.trend.cafall)}</span>
            ) : (
              <span>{t(n_caf.statistic.trend[activeFilter.toLowerCase()])}</span>
            )}
          </h2>
          <TrendGraph trend={graphData} activeFilter={activeFilter} showDetail={showDetail} />
        </article>
      </section>
    )
  }
}

export default TrendGraphSection

const styles = {
  filters: {
    box: `no-scrollbar mb-[16px] flex w-full gap-[8px] overflow-auto`,
    item: (active: boolean) =>
      classNames(
        `text-h5 flex-shrink-0 rounded-[8px] border-[1.5px] py-[8px] px-[12px] font-medium capitalize leading-[18px]`,
        active
          ? `border-purple-500 bg-purple-50 text-purple-500`
          : `border-gray-200 bg-white bg-opacity-20 text-gray-500`
      ),
    item_pc: (active: boolean) =>
      classNames(
        `text-h5 flex-shrink-0 cursor-pointer rounded-[8px] border py-[8px] px-[12px] font-medium capitalize leading-[18px]`,
        active
          ? `border-purple-500 bg-purple-50 text-purple-500`
          : `border-gray-100 bg-white bg-opacity-20 text-gray-500`
      ),
  },
  box: `rounded border border-gray-100 bg-white p-[16px] shadow-sm`,
  box_pc: `w-full rounded border border-gray-100 bg-white p-[16px] shadow-sm`,
  top: {
    title: `text-h5 flex items-center gap-[8px] font-medium capitalize`,
    title_pc: `flex items-center gap-[8px] text-[16px] font-bold capitalize text-gray-700`,
  },
}
